/**
 * Tutorial Steps Configuration
 * 
 * Defines all tutorial steps for the interactive guided tour.
 * Each step highlights a specific UI element and provides instructions
 * to help users learn how to use 8bitcomposer.
 * 
 * @module config/tutorialSteps
 */

import type { TutorialStep } from '../types/tutorial';

/**
 * Tutorial Steps Array
 * 
 * Contains all 15 tutorial steps in sequential order.
 * Each step guides the user through a specific feature or concept.
 * 
 * Pedagogical Progression:
 * - Foundation (Steps 1-4): Basic grid interaction and note creation
 * - Customization (Steps 5-8): Instruments, volume, effects, theme
 * - Playback (Steps 9-10): Controls and timing
 * - Advanced (Steps 11-14): AI, templates, export, share
 * - Conclusion (Step 15): Summary and next steps
 * 
 * Steps:
 * 1. Welcome - Introduction to the tutorial
 * 2. Grid Basics - How to add notes by clicking cells
 * 3. Note Editing - How to edit note pitch values
 * 4. Keyboard Shortcuts - Using W/S keys for pitch adjustment
 * 5. Instruments - Changing instrument types
 * 6. Volume Control - Adjusting channel volume
 * 7. Audio Effects - Using the effects panel (NEW)
 * 8. Theme Customization - Switching visual themes (NEW)
 * 9. Playback Controls - Play, pause, stop, and loop
 * 10. BPM & Pattern Length - Adjusting tempo and pattern length
 * 11. AI Generation - Using AI to create music
 * 12. Templates - Loading pre-made compositions
 * 13. Export - Saving compositions as WAV files
 * 14. Share - Generating shareable links
 * 15. Completion - Tutorial finished message
 */
export const tutorialSteps: TutorialStep[] = [
  // Step 1: Welcome
  {
    id: 'welcome',
    title: '🎵 Welcome to 8bitcomposer!',
    content: `Welcome to the interactive tutorial! 

This guide will teach you everything you need to know to create amazing 8-bit music.

**What you'll learn:**
• 🎹 Creating and editing notes on the grid
• 🎸 Choosing instruments and customizing sounds
• 🎛️ Adding audio effects to enhance your music
• 🎨 Customizing the visual theme
• ▶️ Controlling playback and timing
• 🤖 Using AI to generate music
• 💾 Exporting and sharing your compositions

**Duration:** About 5-10 minutes

Let's get started! Click "Next" to begin your musical journey.`,
    targetSelector: '#main-content',
    position: 'center',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 2: Grid Basics
  {
    id: 'grid-basics',
    title: '🎹 The Music Grid',
    content: `This is your composition grid! Each row represents a different instrument channel, and each column is a step in time.

**How to add notes:**
• Click any cell to toggle a note on or off
• Active notes will light up and play when that step is reached
• The grid plays from left to right, looping continuously

**Try it now:**
Click a few cells to create your first pattern! Don't worry about making mistakes - you can always click again to remove a note.`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 3: Note Editing
  {
    id: 'note-editing',
    title: '🎼 Editing Note Pitch',
    content: `Each note can have a different pitch to create melodies!

**To change a note's pitch:**
• Right-click on any active note (cell with a dot)
• Enter a note value like **C4**, **D#5**, **F3**, or **A2**
• The letter is the note name (C, D, E, F, G, A, B)
• The number is the octave (higher = higher pitch)
• Use **#** for sharps (e.g., C#4, F#5)

**Tip:** Middle C is **C4** - a great starting point for melodies!

Try editing a note now to hear the difference.`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 4: Keyboard Shortcuts
  {
    id: 'keyboard-shortcuts',
    title: '⌨️ Keyboard Shortcuts',
    content: `Speed up your workflow with keyboard shortcuts!

**Quick pitch adjustment:**
• Hover over any active note
• Press **W** to increase pitch (go up one semitone)
• Press **S** to decrease pitch (go down one semitone)

This is much faster than right-clicking once you get the hang of it!

**Other useful shortcuts:**
• **Spacebar** - Play/Pause playback
• **Delete/Backspace** - Clear selected notes

Try using **W** and **S** on a note now!`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 5: Instruments
  {
    id: 'instruments',
    title: '🎸 Choosing Instruments',
    content: `Each row has its own instrument! 8bitcomposer offers classic 8-bit waveforms:

**Available instruments:**
• **Square Wave** - Classic chiptune lead sound, bright and punchy
• **Sawtooth** - Harsh, buzzy tone perfect for bass lines
• **Triangle** - Smooth, mellow tone for melodies
• **Sine** - Pure, soft tone for subtle backgrounds
• **Noise** - Percussion and drum sounds

**How to change:**
Use the dropdown on the left side of any row to select a different instrument.

Try switching instruments now and listen to how it changes the character of your notes!`,
    targetSelector: '.row-controls',
    position: 'right',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 6: Volume Control
  {
    id: 'volume-control',
    title: '🔊 Volume Control',
    content: `Fine-tune your mix with volume controls!

**Volume slider:**
• Drag the slider to adjust the volume for each channel
• Lower volume for background elements
• Higher volume for lead melodies
• Use the **mute button** to temporarily silence a channel

**Mixing tips:**
• Not everything should be at maximum volume
• Create depth by varying volume levels
• Lead melodies typically sit louder in the mix
• Bass and drums provide the foundation

Try adjusting the volume on different rows to create a balanced mix!`,
    targetSelector: '.row-controls',
    position: 'right',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 7: Audio Effects (NEW)
  {
    id: 'effects-panel',
    title: '🎛️ Audio Effects',
    content: `Add professional audio effects to enhance your instruments!

**Effects Panel:**
Click the **FX** button on any row to open the effects panel. Each row can have up to 5 effects in a chain.

**Available effects:**
• **Reverb** - Add space and depth to your sound
• **Delay** - Create echoes and rhythmic repeats
• **Chorus** - Thicken and widen the sound
• **Bitcrusher** - Add lo-fi, crunchy digital distortion
• **Overdrive** - Add warmth and saturation
• **EQ** - Shape the frequency balance

**How to use:**
• Click an empty slot to add an effect
• Adjust parameters with the sliders
• Use the power button to bypass effects
• Click the X button to remove effects

Try adding a reverb effect to create depth in your sound!`,
    targetSelector: '.effects-toggle-btn-compact',
    position: 'right',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 8: Theme Customization (NEW)
  {
    id: 'theme-toggle',
    title: '🎨 Theme Customization',
    content: `Customize the visual appearance of 8bitcomposer!

**Theme Toggle:**
Click the theme button in the navbar to switch between visual themes.

**Available themes:**
• **Retro Theme** - Classic 8-bit pixel art aesthetic with nostalgic colors
• **Modern Theme** - Contemporary, sleek design with smooth gradients

**Features:**
• Your theme preference is saved automatically
• All features work the same in both themes
• Switch anytime to match your mood or preference

Try switching themes now to see the difference! Each theme offers a unique visual experience while maintaining full functionality.`,
    targetSelector: '.theme-toggle-switch',
    position: 'bottom',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 9: Playback Controls
  {
    id: 'playback-controls',
    title: '▶️ Playback Controls',
    content: `Control your composition playback with these buttons:

**Playback buttons:**
• **Play** ▶️ - Start playback from current position
• **Pause** ⏸️ - Pause playback (resume with Play)
• **Stop** ⏹️ - Stop and return to beginning
• **Loop** 🔁 - Toggle continuous looping (on by default)

**Keyboard shortcut:** Press **Spacebar** to quickly play/pause!

Try playing your composition now to hear what you've created. The playback indicator will show your current position in the pattern.`,
    targetSelector: '#controls-section',
    position: 'bottom',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 10: BPM & Pattern Length
  {
    id: 'bpm-steps',
    title: '⏱️ Tempo and Pattern Length',
    content: `Customize your composition's timing and structure:

**BPM (Beats Per Minute):**
• Controls the speed/tempo of your music
• Lower BPM (60-90) = slower, more relaxed feel
• Medium BPM (90-140) = moderate, danceable tempo
• Higher BPM (140-180+) = fast, energetic pace
• Default is 120 BPM

**Pattern Length (Steps):**
• Controls how many steps are in your pattern
• More steps = longer, more complex patterns
• Fewer steps = shorter, tighter loops
• Default is 16 steps (one bar in 4/4 time)

Try adjusting these values to change the feel of your composition!`,
    targetSelector: '#bpm-input',
    position: 'bottom',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 11: AI Generation
  {
    id: 'ai-generation',
    title: '🤖 AI Music Generation',
    content: `Let AI help you create music instantly!

**How it works:**
• Describe the music you want in the text box
• Click **Generate** to create a composition
• AI will fill the grid with notes matching your description
• Use **Fresh Slate** to clear the grid before generating

**Example prompts:**
• "upbeat battle theme with drums"
• "peaceful village music"
• "spooky dungeon ambience"
• "energetic boss fight music"
• "retro platformer level music"

**Requirements:**
• Sign in with your account to use AI generation
• Each generation uses credits from your account
• You can edit AI-generated music just like manual compositions

AI generation is a great way to get started or overcome creative blocks!`,
    targetSelector: '#generate-section',
    position: 'bottom',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 12: Templates
  {
    id: 'templates',
    title: '📚 Template Library',
    content: `Start with pre-made compositions to learn and get inspired!

**Templates are great for:**
• Learning common musical patterns and structures
• Getting inspiration for your own compositions
• Starting with a solid foundation to build upon
• Understanding how different instruments work together

**How to use:**
• Click the **Templates** button to browse the library
• Choose a template that interests you
• Load it into the grid
• Modify it to make it your own unique creation
• Templates can be filtered by category and difficulty level

**Tip:** Don't be afraid to experiment! You can always load a new template or use Fresh Slate to start over.`,
    targetSelector: '.template-button',
    position: 'bottom',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 13: Export
  {
    id: 'export',
    title: '💾 Export to WAV',
    content: `Save your composition as a high-quality audio file!

**How to export:**
• Click the **Export WAV** button
• Your composition will be rendered to a WAV file
• The file will automatically download to your computer
• Use it in videos, games, podcasts, or anywhere else!

**What gets exported:**
• The entire pattern plays through once
• All active channels with their current settings
• All effects applied to each channel
• Current BPM and instrument choices
• High-quality uncompressed audio (WAV format)

**Tip:** Play through your composition a few times before exporting to make sure everything sounds perfect!`,
    targetSelector: '#export',
    position: 'top',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 14: Share
  {
    id: 'share',
    title: '🔗 Share Your Music',
    content: `Share your compositions with the world!

**How sharing works:**
• Click the **Share** button
• A unique URL will be generated for your composition
• Anyone with the link can listen to your music
• Shared compositions are view-only (others can't edit your original)

**Great for:**
• Showing off your creations to friends and family
• Getting feedback from the community
• Collaborating and sharing ideas
• Building a portfolio of your work
• Embedding in websites or social media

**How it works:** Your composition data is encoded directly in the URL, so the link will always work - no server storage needed!`,
    targetSelector: '#share-btn',
    position: 'top',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 15: Completion
  {
    id: 'completion',
    title: '🎉 Tutorial Complete!',
    content: `Congratulations! You've mastered the essentials of 8bitcomposer!

**You now know how to:**
✓ Create and edit notes on the grid
✓ Choose instruments and customize sounds
✓ Adjust volume levels for a balanced mix
✓ Add audio effects to enhance your music
✓ Customize the visual theme to your preference
✓ Control playback, tempo, and pattern length
✓ Use AI to generate music from text descriptions
✓ Load templates for inspiration and learning
✓ Export compositions as WAV files
✓ Share your creations with others via URL

**What's next?**
• 🎨 Experiment with different instrument combinations
• 🎵 Try creating a full song with multiple patterns
• 🎛️ Explore different effect chains and parameters
• 📚 Browse the template library for inspiration
• 🤖 Use AI generation to overcome creative blocks
• 🌟 Share your best creations with the community

**Remember:** The best way to learn is by doing. Don't be afraid to experiment and make mistakes - that's how you discover your unique sound!

**Need help?** You can restart this tutorial anytime from the help menu.

Happy composing! 🎵✨`,
    targetSelector: '#main-content',
    position: 'center',
    allowInteraction: false,
    waitForAction: false,
  },
];

/**
 * Get tutorial step by ID
 * 
 * @param id - Step ID to find
 * @returns Tutorial step or undefined if not found
 */
export function getTutorialStepById(id: string): TutorialStep | undefined {
  return tutorialSteps.find(step => step.id === id);
}

/**
 * Get tutorial step by index
 * 
 * @param index - Step index (0-based)
 * @returns Tutorial step or undefined if index out of bounds
 */
export function getTutorialStepByIndex(index: number): TutorialStep | undefined {
  return tutorialSteps[index];
}

/**
 * Get total number of tutorial steps
 * 
 * @returns Total number of steps
 */
export function getTutorialStepCount(): number {
  return tutorialSteps.length;
}
